import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config/apiConfig';

export const PermissionContext = createContext();

export const PermissionProvider = ({ children }) => {
    const [permissions, setPermissions] = useState(() => {
        const saved = sessionStorage.getItem('permissions');
        return saved ? JSON.parse(saved) : {};
    });
    const [company, setCompany] = useState(() => {
        const saved = sessionStorage.getItem('company');
        return saved ? JSON.parse(saved) : null;
    });
    const [user, setUser] = useState(null);
    const [categories, setCategories] = useState(() => {
        const saved = sessionStorage.getItem('categories');
        return saved ? JSON.parse(saved) : [];
    });
    const [vouchers, setVouchers] = useState(() => {
        const saved = sessionStorage.getItem('vouchers');
        return saved ? JSON.parse(saved) : [];
    });
    const [loading, setLoading] = useState(() => {
        // If we have cached permissions and user, we can skip the initial blocking load
        return !sessionStorage.getItem('permissions') || !sessionStorage.getItem('user');
    });
    const [appLoading, setAppLoading] = useState(false);

    const fetchMyPermissions = async () => {
        try {
            const userStr = sessionStorage.getItem('user');
            const token = sessionStorage.getItem('token');

            if (!userStr || !token) {
                setPermissions({});
                setCompany(null);
                setLoading(false);
                return;
            }

            const user = JSON.parse(userStr);
            setUser(user);
            const currentUserId = user.id || user._id || user.userId;
            if (!currentUserId) {
                setPermissions({});
                setCompany(null);
                setLoading(false);
                return;
            }

            // 1. Prepare promises for parallel execution
            const companyId = user.companyId || user.company_id;
            const fetchPromises = [];

            // Permission promise
            fetchPromises.push(
                axios.get(`${API_BASE_URL}/user-permissions/${currentUserId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(err => {
                    console.error("Error fetching permissions:", err);
                    return { data: { permissions: user.role === 'SUPER_ADMIN' ? { isSuperAdmin: true } : {} } };
                })
            );

            // Company promise (if applicable)
            if (companyId) {
                fetchPromises.push(
                    axios.get(`${API_BASE_URL}/company/${companyId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    }).catch(err => {
                        console.error("Error fetching company details:", err);
                        return { data: null };
                    })
                );
            }

            // 2. Wait for both in parallel
            const [permResponse, companyResponse] = await Promise.all(fetchPromises);

            // 3. Process results
            if (companyResponse && companyResponse.data) {
                setCompany(companyResponse.data);
                sessionStorage.setItem('company', JSON.stringify(companyResponse.data));
            }

            if (permResponse && permResponse.data && permResponse.data.permissions) {
                let fetchedPerms = permResponse.data.permissions;
                if (typeof fetchedPerms === 'string') {
                    try {
                        fetchedPerms = JSON.parse(fetchedPerms);
                    } catch (e) {
                        fetchedPerms = {};
                    }
                }
                const finalPerms = (user.role === 'SUPER_ADMIN') ? { ...fetchedPerms, isSuperAdmin: true } : fetchedPerms;
                setPermissions(finalPerms);
                sessionStorage.setItem('permissions', JSON.stringify(finalPerms));
                // Refresh categories and vouchers silently in background
                fetchCategories();
                fetchVouchers();
            } else if (user.role === 'SUPER_ADMIN') {
                const finalPerms = { isSuperAdmin: true };
                setPermissions(finalPerms);
                sessionStorage.setItem('permissions', JSON.stringify(finalPerms));
                // Refresh categories and vouchers silently in background
                fetchCategories();
                fetchVouchers();
            } else {
                setPermissions({});
                sessionStorage.removeItem('permissions');
            }
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setPermissions({});
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const res = await axios.get(`${API_BASE_URL}/category?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.categories) {
                setCategories(res.data.categories);
                sessionStorage.setItem('categories', JSON.stringify(res.data.categories));
            } else if (Array.isArray(res.data)) {
                setCategories(res.data);
                sessionStorage.setItem('categories', JSON.stringify(res.data));
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchVouchers = async () => {
        try {
            const token = sessionStorage.getItem('token');
            if (!token) return;
            const res = await axios.get(`${API_BASE_URL}/voucher-type?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data && res.data.voucherTypes) {
                setVouchers(res.data.voucherTypes);
                sessionStorage.setItem('vouchers', JSON.stringify(res.data.voucherTypes));
            } else if (Array.isArray(res.data)) {
                setVouchers(res.data);
                sessionStorage.setItem('vouchers', JSON.stringify(res.data));
            }
        } catch (err) {
            console.error("Error fetching vouchers:", err);
        }
    };

    useEffect(() => {
        const init = async () => {
            // Start all 3 fetches in parallel
            const permissionsPromise = fetchMyPermissions();
            const categoriesPromise = fetchCategories();
            const vouchersPromise = fetchVouchers();

            // We ONLY wait for permissions to hide the global "Please wait" overlay.
            // This allows the app to show the dashboard/menu immediately once we know the user's role.
            // Categories and vouchers will continue loading in the background.
            await permissionsPromise;
        };
        init();
    }, []);

    // Function to check if a user has a specific permission
    const hasPermission = useCallback((moduleName, action) => {
        const storedUser = sessionStorage.getItem('user');
        const currentUser = user || (storedUser ? JSON.parse(storedUser) : null);
        const userRole = currentUser?.role?.toUpperCase();

        if (permissions.isSuperAdmin || userRole === 'SUPER_ADMIN') {
            const modulePerms = permissions[moduleName];
            // If super admin has a specific permission set for this module/action, respect it.
            // This allows them to hide/show their own dashboard cards.
            if (modulePerms && modulePerms[action] !== undefined) {
                return modulePerms[action] === 1;
            }
            return true; // Default to true for super admins
        }

        // Company Admin (ADMIN / COMPANY_ADMIN) has all permissions for User and Plan Payment modules by default
        if ((userRole === 'ADMIN' || userRole === 'COMPANY_ADMIN') && (moduleName?.toLowerCase() === 'user' || moduleName?.toLowerCase() === 'plan payment' || moduleName?.toLowerCase() === 'planpayment')) {
            return true;
        }

        const modulePerms = permissions[moduleName];
        if (!modulePerms) {
            return false;
        }
        return modulePerms[action] === 1;
    }, [permissions, user]);

    const contextValue = useMemo(() => ({
        permissions,
        company,
        user,
        categories,
        vouchers,
        hasPermission,
        fetchMyPermissions,
        fetchCategories,
        fetchVouchers,
        loading,
        appLoading,
        setAppLoading
    }), [permissions, company, categories, vouchers, hasPermission, loading, appLoading]);

    return (
        <PermissionContext.Provider value={contextValue}>
            {(loading || appLoading) && (
                <div className="loading-overlay">
                    <div className="loading-spinner"></div>
                    <div className="loading-text">Please wait...</div>
                </div>
            )}
            {children}
        </PermissionContext.Provider>
    );
};

export const usePermissionContext = () => useContext(PermissionContext);
